import type { DomainMessagesTypes, Message } from 'shared/domain/messages/message';
import { isPlainObject } from 'shared/utils/object';
import { channelClient } from '../channelContext';
import { RequestPolicies } from '../requestPolicy';
import type { ChannelContractShape, ContractEvent } from '../contract';
import type { ChannelUnsubscribe } from '../types';
import type { EntityContract, EntityContractShape } from './entityContract';

export interface EntityClient<
  TModel,
  TEvents extends ChannelContractShape['events'],
  TRequests extends ChannelContractShape['requests'] = Record<never, never>,
> {
  /** The whole collection, once the worker has it downloaded. */
  getAll(): Promise<TModel[]>;
  /** Asks the worker to sync the entity from the API. */
  sync(): void;
  /** Every event of the entity; `switch (event.type)` narrows `event.data`. */
  onEvent(
    handler: (event: ContractEvent<EntityContractShape<TModel, TEvents, TRequests>>) => void
  ): ChannelUnsubscribe;
  /** The items when `event` is the complete collection after a sync, otherwise `undefined`. */
  readCollection(event: Message): TModel[] | undefined;
}

interface CollectionPayload<TModel> {
  hasAll?: boolean;
  items: TModel[];
}

function isCollectionPayload<TModel>(data: unknown): data is CollectionPayload<TModel> {
  return isPlainObject(data) && Array.isArray(data.items);
}

/** The items when `event` is the complete collection (`collectionEvent` with `hasAll`), otherwise `undefined`. */
export function readCollectionEvent<TModel>(
  collectionEvent: DomainMessagesTypes,
  event: Message
): TModel[] | undefined {
  return event.type === collectionEvent && isCollectionPayload<TModel>(event.data) && Boolean(event.data.hasAll)
    ? event.data.items
    : undefined;
}

/** Read side of an entity served by the worker; modules add their mutations next to it. */
export function createEntityClient<
  TModel,
  TEvents extends ChannelContractShape['events'],
  TRequests extends ChannelContractShape['requests'] = Record<never, never>,
>(contract: EntityContract<TModel, TEvents, TRequests>): EntityClient<TModel, TEvents, TRequests> {
  return {
    getAll: () => channelClient.request(contract, 'getAll', undefined, RequestPolicies.bulkRead),
    sync: () => channelClient.notify(contract, 'sync', undefined),
    onEvent: (handler) => channelClient.onEvent(contract, handler),
    readCollection: (event) => readCollectionEvent<TModel>(contract.collectionEvent, event),
  };
}
